import { KeyListener } from '@words-happen/ui-components';
import React, { useCallback, useContext, useMemo, useState } from 'react';

export interface ModalProviderProps {
  children: React.ReactNode;
}

export enum Modal {
  NONE = 0,
  SEARCH = 1,
  STOCK_TAG = 2,
}

type ModalState = {
  visible: Modal;
};

type SetVisible = (visible: Modal) => void;

const defaultModalState: ModalState = {
  visible: Modal.NONE,
};

export type ModalControllerState = ModalState & { closeAll: () => void; setVisible: SetVisible };

export const ModalControllerContext = React.createContext<ModalControllerState>({
  ...defaultModalState,
  closeAll: () => {
    /* noop */
  },
  setVisible: () => {
    /* noop */
  },
});

export const ModalController: React.FC<ModalProviderProps> = ({ children }: ModalProviderProps) => {
  const [state, setState] = useState<ModalState>({ visible: Modal.NONE });

  const contextState = useMemo<ModalControllerState>(
    () => ({
      ...state,
      closeAll: () => {
        setState({
          ...state,
          visible: Modal.NONE,
        });
      },
      setVisible: (modal: Modal) => {
        setState({
          ...state,
          visible: modal,
        });
      },
    }),
    [state],
  );

  const handleShowSearch = useCallback(
    (event: KeyboardEvent) => {
      contextState.setVisible(Modal.SEARCH);
      event.preventDefault();
    },
    [contextState],
  );

  const handleShowStockTag = useCallback(
    (event: KeyboardEvent) => {
      contextState.setVisible(Modal.STOCK_TAG);
      event.preventDefault();
    },
    [contextState],
  );

  const handleHideModals = useCallback(
    (event: KeyboardEvent) => {
      contextState.setVisible(Modal.NONE);
      event.preventDefault();
    },
    [contextState],
  );

  return (
    <ModalControllerContext.Provider value={contextState}>
      <KeyListener nonInteractive keys="/" onKeyDown={handleShowSearch} />
      <KeyListener nonInteractive keys="t" onKeyDown={handleShowStockTag} />
      <KeyListener keys="Escape" onKeyDown={handleHideModals} />
      {children}
    </ModalControllerContext.Provider>
  );
};

export const useModalState = () => {
  const contextState = useContext(ModalControllerContext);
  return contextState;
};
